import {
  CharacterFlow,
  type ICharacter,
  NotificationType,
  useCharacter,
  useLoadCharacter,
  useLorebook,
  useTranslation,
} from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { CHARACTER_MODERATION_STATUS, CHARACTER_VISIBILITY } from "@next-platform/client-common-core/utils/sharedTypes";
import { Spinner, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { Lorebook } from "@next-platform/packages-ts-types";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { LocalizedLink, PageLayout, PageSize } from "@/components";
import TextStyles from "@/components/common/TextStyles";
import { Grid } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { EXTERNAL_ROUTES_FOR_APPLICATION, FEATURE_FLAGS, ROUTES } from "@/constants";
import { useFeatureFlag } from "@/hooks";
import useToast from "@/hooks/useToast";
import CreateCharacterHeroSection from "@/pages/CreateCharacter/v2/components/CreateCharacterHeroSection";
import CreateCharacterLockedOverlay from "@/pages/CreateCharacter/v2/components/CreateCharacterLockedOverlay";
import CreateCharacterPerksSection from "@/pages/CreateCharacter/v2/components/CreateCharacterPerksSection";
import ModerationStatus from "@/pages/CreateCharacter/v2/components/ModerationStatus";
import CreateGroupForm from "@/pages/Groupchat/components/CreateGroupForm";
import buildGroupChatRequest from "@/pages/Groupchat/utils/buildGroupChatRequest";
import persistEditedGroupChat from "@/pages/Groupchat/utils/persistEditedGroupChat";
import useAuth from "@/services/useAuth";
import { IDraftGroupCharacter } from "@/types";
import { cn } from "@/utils";

// This component follows the footsteps of its cousin, the create chatbot component V2.
// However, instead of creating a charatcter it creates a room and involves already existing bots that user may add to common conversation he/she constructs
// many features from create chatbot V2 were reused, however, in spite of functional similarities, the API endpoints and data shapes of this component are meant to be different
// To see this component, enable Group chat feature flag

const groupchatEditLogger = appLogger.withTag("GroupchatEdit");

export function GroupchatEdit() {
  const {
    onUserCreateCharacter,
    onUserUpdateCharacter,
    isLoading: isCharacterLoading,
    loadingType: characterLoadingType,
    errorMessage: submitError,
    errorCode,
  } = useCharacter();
  const { onUserAttachLorebook, onUserDetachLorebook } = useLorebook();
  const navigate = useNavigate();
  const { characterId } = useParams();
  const [stateStoredData, setStateStoredData] = useState<IDraftGroupCharacter | null>(null);
  const { isAuthenticated, isLoading } = useAuth();
  const applicationId = basicAppConfig.applicationId;
  const { displayToast } = useToast();
  const { hasFeatureFlag: hasGroupLorebookFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.RELEASE_GROUP_LOREBOOK_ATTACHMENT_WEB);

  const { t } = useTranslation();
  const [stateIsSubmitForManualReview, setStateIsSubmitForManualReview] = useState(false);
  const [stateIsLorebookOperationInProgress, setStateIsLorebookOperationInProgress] = useState(false);
  const isSubmitting =
    (isCharacterLoading && (characterLoadingType === CharacterFlow.CREATE_CHARACTER || characterLoadingType === CharacterFlow.UPDATE_CHARACTER)) ||
    stateIsLorebookOperationInProgress;

  const { characterData } = useLoadCharacter({ characterId: characterId || "" });
  const character = characterData as ICharacter | null;

  useEffect(() => {
    if (characterId && characterData) {
      setStateStoredData({
        ...characterData,
        sub_characters: characterData.sub_characters?.map((char) => ({
          id: char.id,
          name: char.name,
          avatar: char.avatar_url,
          avatarIsNsfw: char.avatar_is_nsfw,
          visibility: char.visibility,
          hasError: false,
        })),
      } as IDraftGroupCharacter);
    }
  }, [characterId, characterData]);

  const updateGroupLorebooks = async (currentLorebooks: Lorebook[], initialLorebooks: Lorebook[]): Promise<{ error: string | null }> => {
    if (!hasGroupLorebookFeatureFlag || !characterId) {
      return { error: null };
    }

    const currentLorebookIds = currentLorebooks.map((lorebook) => lorebook.id);
    const initialLorebookIds = initialLorebooks.map((lorebook) => lorebook.id);
    const lorebooksToDetach = initialLorebookIds.filter((lorebookId) => !currentLorebookIds.includes(lorebookId));
    const lorebooksToAttach = currentLorebookIds.filter((lorebookId) => !initialLorebookIds.includes(lorebookId));

    if (lorebooksToDetach.length > 0 || lorebooksToAttach.length > 0) {
      setStateIsLorebookOperationInProgress(true);
    }

    try {
      const detachResults = await Promise.all(lorebooksToDetach.map((lorebookId) => onUserDetachLorebook(lorebookId, characterId)));
      const detachFailure = detachResults.find((result) => result?.error != null);

      if (detachFailure?.error != null) {
        throw new Error(`Failed to detach lorebook: ${detachFailure.error}`);
      }

      const attachResults = await Promise.all(lorebooksToAttach.map((lorebookId) => onUserAttachLorebook(lorebookId, { characterId })));
      const attachFailure = attachResults.find((result) => result?.error != null);

      if (attachFailure?.error != null) {
        throw new Error(`Failed to attach lorebook: ${attachFailure.error}`);
      }

      return { error: null };
    } catch (lorebookError) {
      groupchatEditLogger.error("Error updating group lorebooks:", lorebookError);
      const error = lorebookError instanceof Error ? lorebookError.message : t("createGroup:toast.error.detachLorebook");
      displayToast({ message: error, type: NotificationType.error });
      return { error };
    } finally {
      setStateIsLorebookOperationInProgress(false);
    }
  };

  const onSave = async (
    props: IDraftGroupCharacter,
    currentLorebooks: Lorebook[],
    initialLorebooks: Lorebook[],
    saveOptions?: { hasGroupContentChanged?: boolean },
  ) => {
    const requestProps = buildGroupChatRequest({
      draftGroupCharacter: props,
      includeClearedGreetingCharacter: Boolean(stateStoredData?.greeting_character_id),
      includeType: !stateStoredData?.id,
      submitForManualReview: stateIsSubmitForManualReview,
    });

    const hasGroupContentChanged = saveOptions?.hasGroupContentChanged ?? true;

    const {
      data: savedGroupChat,
      error,
      errorPayload,
    } = stateStoredData?.id
      ? await persistEditedGroupChat({
          groupId: stateStoredData.id,
          hasGroupContentChanged,
          requestProps,
          currentLorebooks,
          initialLorebooks,
          updateGroup: onUserUpdateCharacter,
          syncLorebooks: updateGroupLorebooks,
        })
      : await onUserCreateCharacter(requestProps);

    if (!error && savedGroupChat?.id && !stateStoredData?.id) {
      const { error: lorebookError } = await updateGroupLorebooks(currentLorebooks, initialLorebooks);

      if (lorebookError) {
        return { ["data"]: savedGroupChat ?? null, error: lorebookError };
      }
    }

    if (error) {
      if (errorPayload?.["data"]?.submitForManualReview === true) {
        setStateIsSubmitForManualReview(true);
      }
      return { ["data"]: savedGroupChat ?? null, error };
    }

    if (savedGroupChat?.id) {
      navigate(ROUTES.groupchats);
    }

    return { ["data"]: savedGroupChat ?? null, error: error ?? null };
  };

  if (isLoading) {
    return <Spinner className={TextStyles.spinner} />;
  }

  return (
    <PageLayout size={PageSize.Medium}>
      {!isAuthenticated && (
        <>
          <CreateCharacterHeroSection />
          <CreateCharacterPerksSection />
        </>
      )}
      <div
        className={cn(
          "flex flex-col relative bg-gray-1 dark:bg-gray-3 p-lg max-mob:p-md rounded-[16px] border border-solid border-gray-5",
          !isAuthenticated && "py-16",
        )}
      >
        {!isAuthenticated ? <CreateCharacterLockedOverlay /> : null}
        <Grid className="flex justify-between flex-row max-mob:flex-col gap-2 my-2">
          <Text variant={TextVariants.HEADING_5_BOLD}>
            {characterId ? <Trans i18nKey="createGroup:page.editTitle" /> : <Trans i18nKey="createGroup:page.title" />}
          </Text>

          <LocalizedLink
            className="flex items-center text-link"
            target="_blank"
            to={EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.group_creation_guide}
            aria-label={t("common:creationGuide")}
          >
            <Trans i18nKey="common:creationGuide" />
          </LocalizedLink>
        </Grid>
        <ModerationStatus
          applicationId={applicationId as string}
          moderationReasonCodes={character?.moderationReport?.moderation_reason_codes}
          actionTakenDescription={character?.moderationReport?.action_taken_description}
          reasonForModeration={character?.moderationReport?.reason_for_moderation}
          moderationStatus={character?.moderationReport?.moderation_status as CHARACTER_MODERATION_STATUS}
          reportsType={character?.reportsType}
          type={character?.type}
        />
        {characterId && !stateStoredData ? (
          <Spinner />
        ) : (
          <CreateGroupForm
            moderationStatus={character?.moderationReport?.moderation_status}
            key={characterId || "edit"}
            id={stateStoredData?.id || ""}
            name={stateStoredData?.name || ""}
            avatarUrl={stateStoredData?.avatar_url || ""}
            greeting={stateStoredData?.greeting || ""}
            greetingCharacterId={stateStoredData?.greeting_character_id}
            visibility={(stateStoredData?.visibility || CHARACTER_VISIBILITY.PRIVATE) as CHARACTER_VISIBILITY}
            title={stateStoredData?.title || ""}
            scenario={stateStoredData?.scenario || ""}
            guidelines={false}
            tags={stateStoredData?.tags || []}
            participants={stateStoredData?.sub_characters}
            onSave={onSave}
            draftId={""}
            characterId={characterId || ""}
            isSubmitting={isSubmitting}
            submitError={submitError || ""}
            errorCode={errorCode}
            submitForManualReview={stateIsSubmitForManualReview}
            setIsSubmitForManualReview={setStateIsSubmitForManualReview}
            isAlreadyDraft={false}
            isEdit={!!characterId}
            maxParticipants={10}
            lorebooks={stateStoredData?.lorebooks || []}
            isLorebookAutoDetached={Boolean(character?.lorebook_auto_detached)}
          />
        )}
      </div>
    </PageLayout>
  );
}

export default GroupchatEdit;
